/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  VStack,
  useToast,
  Container,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import Swal from 'sweetalert2';
import { useAddPlan } from './hooks/useAddPlan';
import { useGetAllPlans } from './hooks/useGetAllPlans';
import { useUpdatePlan } from './hooks/useUpdatePlan';
import { useGetPlanById } from './hooks/useGetPlanById';
import { useDeletePlan } from './hooks/useDeletePlan';
import PlanForm from './components/PlanForm';
import PlanList from './components/PlanList';
import PlanDetailsModal from './components/PlanDetailsModal';
import { useQueryClient } from '@tanstack/react-query';

const PlansManagement = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');

  const resetForm = () => {
    setCategory('');
    setContent('');
    setSelectedPlan(null);
    setShowForm(false);
  };

  const { mutate: createPlan, isPending } = useAddPlan({
    onSuccess: resetForm,
  });

  const { mutate: updatePlan, isPending: isPendingUpdated } = useUpdatePlan({
    onSuccess: resetForm,
  });

  const { mutate: deletePlanMutation } = useDeletePlan();

  const handleSave = () => {
    if (!category || !content) {
      toast({
        title: 'Input Required',
        description: 'Please complete all fields before publishing.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      id: selectedPlan?._id,
      name: category,
      details: content,
    };

    if (selectedPlan?._id) {
      updatePlan(payload);
    } else {
      createPlan({
        name: category,
        details: content,
      });
    }
  };

  const { data, isLoading, isError } = useGetAllPlans();

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This plan will be removed from the mobile app.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#319795',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      reverseButtons: true,
      background: swalBackground,
      color: swalColor,
      borderRadius: '15px',
    }).then((result) => {
      if (result.isConfirmed) {
        deletePlanMutation(id, {
          onSuccess: () => {
            Swal.fire({
              title: 'Deleted!',
              text: 'Plan has been deleted successfully.',
              icon: 'success',
              timer: 1500,
              showConfirmButton: false,
              background: swalBackground,
              color: swalColor,
            });

            queryClient.invalidateQueries(['plans-list']);
          },
        });
      }
    });
  };

  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    setShowForm(true);
  };

  const { data: planById, isLoading: isLoadingById } = useGetPlanById(
    selectedPlan?._id,
  );

  useEffect(() => {
    if (planById) {
      setCategory(planById.name);
      setContent(planById.details);
    }
  }, [planById]);

  const viewDetails = (plan) => {
    setSelectedPlan(plan);
    onOpen();
  };

  const bgGradient = useColorModeValue(
    'linear(to-br, #f8fbfb, #f0f4f8)',
    'linear(to-br, gray.900, gray.800)',
  );
  const headerBg = useColorModeValue('white', 'gray.800');
  const swalBackground = useColorModeValue('#ffffff', '#1A202C');
  const swalColor = useColorModeValue('#000000', '#ffffff');

  return (
    <Box bgGradient={bgGradient} minH="100vh" py={10}>
      <Container maxW="container.xl">
        {/* Header */}
        <Flex
          justify="space-between"
          align="center"
          mb={10}
          bg={headerBg}
          p={6}
          borderRadius="2xl"
          boxShadow="sm"
        >
          <VStack align="left" spacing={0}>
            <Heading
              size="lg"
              color={useColorModeValue('teal.700', 'teal.200')}
            >
              Plans Management
            </Heading>
          </VStack>

          {!showForm && (
            <Button
              leftIcon={<AddIcon />}
              colorScheme="teal"
              onClick={() => setShowForm(true)}
              boxShadow="md"
            >
              Create New Plan
            </Button>
          )}
        </Flex>

        {showForm ? (
          <PlanForm
            category={category}
            setCategory={setCategory}
            content={content}
            setContent={setContent}
            handleSave={handleSave}
            onBack={resetForm}
          />
        ) : (
          <PlanList
            data={data}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            viewDetails={viewDetails}
            setShowForm={setShowForm}
          />
        )}

        <PlanDetailsModal
          isOpen={isOpen}
          onClose={onClose}
          selectedPlan={selectedPlan}
        />
      </Container>
    </Box>
  );
};

export default PlansManagement;
